import clsx from "clsx";
import { ArrowUpRight, BarChart3, Calculator, FileText, Images, Layers, List, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { basisLabel, type MoneyFormatter } from "../../shared/money";
import { ChoiceField, TextField } from "../../web/components/fields";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { RecordWindow } from "../../web/components/RecordWindow";
import { CheckedPill } from "./CheckedMark";
import { PhotosTab, usePhotos } from "./PhotosTab";
import { AddPicker } from "../../web/components/AddPicker";
import { WindowPopup } from "../../web/components/WindowPopup";
import { matchesSearch, NeedsAttention } from "./ListToolbar";
import { attentionText, groupAttention, type ItemProblem, type ItemProblemId, tintsRow } from "./attention";
import { OnlyPill, ProblemChips, type ProblemKind, problemKinds, useLiveOnly, withProblem } from "./ProblemChips";
import { InlinePicker } from "../../web/components/InlinePicker";
import { useConfirm, useWindowConfirm } from "../../web/components/Confirm";
import { Badge, Box, ErrorNotice, IssueChip, Issues, Notice, Spinner } from "../../web/components/ui";
import { alreadyShown, markShown, nextShowToken } from "../../web/lib/flash";
import { useMoney } from "../../web/lib/money";
import { can, freeGroupName, groupTaxBasis, itemName, itemTax, membersOf, pricedMembersOf, updateEach, usePriceListSaver, useRowUpdates, whyNot } from "./data";
import { inUse, isProduct, type ItemPricing, type MaterialRow, meta, type PriceListData, type PricingRule, type GroupKind, groupKind, type GroupRow, type GroupTax, usesGroup } from "./meta";
import { HiddenMark, retiredName } from "./HiddenMark";
import { Link } from "wouter";
import type { NameTagConfig } from "./names";
import { ServiceM8Name } from "./NameTags";
import { calcPrice, keepShownAmounts, missingCost, type NeedsCheck, needsCheck } from "./pricing";
import { PricingEditor } from "./PricingEditor";
import { TiersCard, type TiersDraft, tiersDraftOf } from "./TiersCard";
import { type GroupDraft, GroupSave, sameDraft } from "./GroupPricing";
import { PricingCard } from "./PricingTab";
import { plural } from "../../shared/words";
import { GroupStatsTab, withStatsTab } from "./StatsTab";
import { useLeftUnsaved, usePriceListWindows, useUnsavedGuard } from "./windows";
import { DuplicatePopup, duplicateAction } from "./DuplicatePopup";
import { describePricing } from "./describe";

/*
 * One price group, in a window like an item's (opened from the Groups list, or an item): its name and pricing (Pricing tab: a
 * change is a draft, saved for all its items at once, with the prices it moves shown first; closing the window asks first) and its
 * products (Products tab: add or take them out; each follows its pricing at once: productsFollow.ts; Needs attention says what
 * needs it, linking there). The group says how its items are taxed (part of its pricing), and its amounts are shown and
 * typed that way ("$100 inc GST"), stored before tax; switching its tax keeps them as typed ($100 ex GST). Delete is in its More
 * menu (asked first): its items keep the pricing.
 */

const TABS = [
    { id: "details", label: "Details", icon: <FileText className="size-5" /> },
    { id: "pricing", label: "Pricing", icon: <Calculator className="size-5" /> },
    // Its products (id kept from when they were items: links open it by it).
    { id: "items", label: "Products", icon: <List className="size-5" /> },
    { id: "stats", label: "Stats", icon: <BarChart3 className="size-5" /> },
    // What's in it (photos.ts): to show a customer.
    { id: "photos", label: "Photos", icon: <Images className="size-5" /> },
];

export function GroupWindow(
    props: Readonly<{
        data: PriceListData;
        id: string;
        tab: string;
        onTab: (tab: string) => void;
        onClose: () => void;
        onOpenItem: (uuid: string) => void;
        /** Just made with a made-up name: its name is ready to type over. */
        rename?: boolean;
    }>,
) {
    const saver = usePriceListSaver();
    const group = props.data.groups.find((g) => g.id === props.id);
    const [error, setError] = useState<Error | null>(null);
    // Duplicating it: its popup open (DuplicatePopup).
    const [duplicating, setDuplicating] = useState(false);
    const windows = usePriceListWindows();
    // A pricing change not saved yet: kept here, so it stays while another tab is open (marked on
    // the Pricing tab), and closing the window asks first, in the window.
    const [draft, setDraft] = useState<GroupDraft | null>(null);
    // ...or (a markup) its tiers, the same way.
    const [tiersDraft, setTiersDraft] = useState<TiersDraft | null>(null);
    // Asking about unsaved changes before leaving them (closing, or opening another group here): a popup in the window (its `Here` in it).
    const windowConfirm = useWindowConfirm();
    // Bumped each time it asks, so the Pricing tab scrolls its draft into view.
    const [reveal, setReveal] = useState(0);
    // The Items tab showing only those with one kind of warning (its chip in Needs attention: ProblemChips.tsx), or null: all.
    const [only, setOnly] = useState<ItemProblemId | null>(null);
    useEffect(() => {
        setDraft(null);
        setTiersDraft(null);
        setOnly(null);
    }, [props.id]);
    const edited = (group !== undefined && draft !== null && !sameDraft(draft, group, props.data.taxRates)) || tiersDraft !== null;
    const unsaved = edited;
    // Its photos, counted on its Photos tab.
    const photoCount = usePhotos("group", props.id).query.data?.length;
    // Those to show in Needs attention: once moved off their tab (windows.ts).
    const left = useLeftUnsaved(props.tab, { pricing: { unsaved: edited, tab: "pricing" } });
    /** Ask before leaving what isn't saved (a popup: leave it, as `label` says, or keep editing), with it (and its save buttons) in view behind it. */
    const ask = (leave: () => void, label: string) => {
        void windowConfirm.confirm({ title: "Leave without saving?", message: UNSAVED_TEXT, confirmLabel: label, cancelLabel: "Keep editing", tone: "danger" }).then((yes) => yes && leave());
        props.onTab("pricing");
        setReveal(nextShowToken());
    };
    // Opening another group here while this one has changes not saved: it stays, flashes (windows.ts), and asks.
    useUnsavedGuard("group", unsaved, (proceed) => ask(proceed, "Discard and open the other one"));

    if (!group) {
        return (
            <RecordWindow kind="group" tone="violet" icon={<Layers className="size-4" />} title="Group not found" tabs={[]} tab="" onTab={() => undefined} onClose={props.onClose}>
                <Notice>This group doesn&apos;t exist any more.</Notice>
            </RecordWindow>
        );
    }
    const tabs = withStatsTab(TABS, props.data);
    const tab = tabs.some((t) => t.id === props.tab) ? props.tab : "details";
    // Its items needing attention (the same checks as the Materials list): marked on the Items tab, said at the top.
    const attention = groupAttention(props.data, group.id);
    const run = (fn: () => Promise<unknown>) => {
        setError(null);
        fn().catch((err: Error) => setError(err));
    };
    return (
        <RecordWindow
            kind="group"
            share
            tone="violet"
            icon={<Layers className="size-4" />}
            title={<span className="truncate">{group.name}</span>}
            label={group.name}
            subtitle={plural(membersOf(props.data, group.id).length, "product")}
            barExtra={<CheckedPill data={props.data} kind="group" id={group.id} onError={setError} />}
            // How many: its products, and its photos.
            tabs={markedTabs(tabs, edited, attention).map((t) => ({ ...t, count: t.id === "items" ? membersOf(props.data, group.id).length : t.id === "photos" ? photoCount : undefined }))}
            tab={tab}
            onTab={props.onTab}
            onClose={() => (unsaved ? ask(props.onClose, "Discard and close") : props.onClose())}
            actions={
                can(props.data, "groups")
                    ? [
                          duplicateAction("group", () => setDuplicating(true)),
                          deleteAction(group.name, () => run(() => saver.deleteGroup(group.id).then(props.onClose))),
                      ]
                    : []
            }
            notices={
                <>
                    <windowConfirm.Here />
                    <ErrorNotice error={error} />
                    <Issues
                        chips={
                            <>
                                {/* Changes not saved yet, once you've moved off their tab and until saved or cancelled, first (as an item's). */}
                                {left.has("pricing") ? <IssueChip tone="unsaved" label="Unsaved pricing" title="Its pricing has changes: save or cancel them." onShow={() => props.onTab("pricing")} /> : null}
                                {/* Its items' warnings, a chip for each kind (every one they have), each its Items tab narrowed to them. */}
                                <ProblemChips
                                    kinds={problemKinds(attention.all, (uuid) => itemNameOf(props.data, uuid))}
                                    onShow={(kind) => {
                                        setOnly(kind);
                                        props.onTab("items");
                                    }}
                                />
                            </>
                        }
                    />
                    {whyNot(props.data, "groups") ? <Notice>{whyNot(props.data, "groups")}</Notice> : null}
                </>
            }
        >
            <div>
                {tab === "details" && <GroupDetails data={props.data} group={group} rename={props.rename} />}
                {tab === "pricing" && <GroupPricing key={group.id} data={props.data} group={group} draft={draft} onDraft={setDraft} tiersDraft={tiersDraft} onTiersDraft={setTiersDraft} reveal={reveal} />}
                {tab === "items" && (
                    <GroupItems key={group.id} data={props.data} group={group} onOpenItem={props.onOpenItem} attention={attention.problems} all={attention.all} only={only} onOnly={setOnly} />
                )}
                {tab === "stats" && <GroupStatsTab id={group.id} onOpenItem={props.onOpenItem} />}
                {tab === "photos" && <PhotosTab data={props.data} kind="group" id={group.id} />}
                <LeaveGuard window={["group", group.id]} when={unsaved} message={`${group.name}: ${UNSAVED_TEXT}`} />
                {duplicating ? (
                    <DuplicatePopup
                        data={props.data}
                        kind="group"
                        name={freeGroupName(props.data, group.name)}
                        withNumber={false}
                        supplierPrices={null}
                        // Its description, pricing and tax; no items (a new group starts empty).
                        onCreate={async (copy) => {
                            // A markup: its tiers; a fixed price: its price and tax.
                            const tiers = group.tiers;
                            const made = await saver.createGroup({ name: copy.name, basePrice: group.rule.basePrice, tiers });
                            await saver.updateGroup(made.id, tiers ? { description: group.description } : { description: group.description, tax: group.tax });
                            windows.openGroup(made.id);
                        }}
                        onClose={() => setDuplicating(false)}
                    />
                ) : null}
            </div>
        </RecordWindow>
    );
}

/** Unsaved: its pricing has changes (what leaving would drop). */
const UNSAVED_TEXT = "Its pricing has changes that aren't saved.";

/** Its tabs, marked: Pricing while it has changes not saved, Items when some need attention. */
function markedTabs<T extends { id: string }>(tabs: readonly T[], unsaved: boolean, attention: { count: number; danger: boolean }) {
    return tabs.map((t) => {
        if (t.id === "pricing") return { ...t, unsaved };
        if (t.id === "items" && attention.count) return { ...t, attention: { danger: attention.danger, label: attentionText(attention.count) } };
        return t;
    });
}

/** Its More menu's Delete group (asked first): its items keep their pricing. */
const deleteAction = (name: string, run: () => void) => ({
    icon: <Trash2 className="size-4" />,
    label: "Delete group",
    help: "Its products keep their pricing. No prices change.",
    danger: true,
    confirm: { title: `Delete ${name}?`, message: "Its products keep its price as their own. No prices change.", confirmLabel: "Delete group" },
    run,
});

/**
 * A group's details: its name (selected to type over when it's just been made: `rename`) and what
 * it's for, a few words for whoever picks a group for an item. Only kept here.
 */
function GroupDetails({ data, group, rename }: Readonly<{ data: PriceListData; group: GroupRow; rename?: boolean }>) {
    const saver = usePriceListSaver();
    const canEdit = can(data, "groups");
    return (
        <Box>
            <TextField
                label="Name"
                selectOnOpen={rename}
                value={group.name}
                disabled={!canEdit}
                validate={(v) => (v ? null : "Give it a name")}
                onSave={(name) => saver.updateGroup(group.id, { name })}
            />
            <TextField
                label="Description"
                multiline
                value={group.description}
                placeholder="What it's for, e.g. which products belong in it"
                disabled={!canEdit}
                hint="Only kept here: never sent to ServiceM8."
                onSave={(description) => saver.updateGroup(group.id, { description })}
            />
        </Box>
    );
}

/**
 * Its pricing: a draft until it's saved for every item in the group (GroupSave).
 * Each time `reveal` goes up (closing the window with the draft unsaved), the draft and its save
 * buttons are scrolled into view.
 */
function GroupPricing(
    props: Readonly<{
        data: PriceListData;
        group: GroupRow;
        draft: GroupDraft | null;
        onDraft: (draft: GroupDraft | null) => void;
        /** A markup's tiers being changed (TiersCard.tsx). */
        tiersDraft: TiersDraft | null;
        onTiersDraft: (draft: TiersDraft | null) => void;
        reveal: number;
    }>,
) {
    const { data, group, draft, onDraft: setDraft } = props;
    // Saving and updating (or showing what couldn't be): its fields wait, so nothing's changed that the run would then drop.
    const [running, setRunning] = useState(false);
    const canEdit = can(data, "groups") && !running;
    const pricing = useRef<HTMLDivElement>(null);
    useEffect(() => {
        // Once per ask (lib/flash.ts): back on this tab, it isn't scrolled to again.
        if (!props.reveal || alreadyShown(props.reveal)) return;
        pricing.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        markShown(props.reveal);
    }, [props.reveal]);
    const { rule, tax } = draft ?? { rule: group.rule, tax: group.tax };
    // A change back to what's saved is no change: the draft goes.
    const toDraft = (next: GroupDraft) => {
        setDraft(sameDraft(next, group, data.taxRates) ? null : next);
        return Promise.resolve();
    };
    const change = (patch: Partial<PricingRule>) => toDraft({ rule: { ...rule, ...patch }, tax });
    // A fixed price is the same for every item in it (no cost involved), so it's shown as an item's is; worked out from each item's cost, it isn't.
    const setPrice = rule.mode === "manual" ? calcPrice({ cost: "0", price_includes_taxes: tax.includesTax }, groupTaxBasis(data, tax).tax, rule)?.price : undefined;
    // Its typed amounts stay as they read ($100 inc GST becomes $100 ex GST): pricing.ts keepShownAmounts.
    const changeTax = (patch: Partial<GroupTax>) => {
        const next = { ...tax, ...patch };
        const basisOf = (t: GroupTax) => {
            const b = groupTaxBasis(data, t);
            return { includesTax: b.includesTax, tax: b.tax };
        };
        return toDraft({ rule: keepShownAmounts(rule, basisOf(tax), basisOf(next)), tax: next });
    };

    // Its kind as drafted (switching is a draft, saved with the rest), else as kept.
    let kind: GroupKind = groupKind(group);
    if (props.tiersDraft) kind = props.tiersDraft.kind;
    else if (draft && group.tiers) kind = "fixedPrice";
    const switchKind = (to: GroupKind) => {
        if (to === kind) return Promise.resolve();
        if (to === "fixedPrice") {
            props.onTiersDraft(null);
            // From a markup: a fixed price to type (taxed as it was set, or the business's way), else back to as kept.
            setDraft(group.tiers ? { rule: { mode: "manual", markupValue: "0", basePrice: "0" }, tax: group.tax } : null);
        } else {
            setDraft(null);
            props.onTiersDraft(tiersDraftOf(group, props.tiersDraft, to));
        }
        return Promise.resolve();
    };
    const kindChoice = (
        <ChoiceField<GroupKind>
            label="Kind"
            value={kind}
            disabled={!canEdit}
            options={[
                { value: "fixedPrice", label: "Fixed price", hint: "The same price for every product in it, taxed as it says." },
                { value: "markup", label: "Markup", hint: "A markup on each product's own cost, before tax. Each keeps its own tax." },
                { value: "tiered", label: "Tiered markup", hint: "A markup by cost: more on cheap parts, less on dear ones." },
            ]}
            onSave={switchKind}
        />
    );

    // A markup: its tiers (its items each priced by their own cost, keeping their own tax), then its prices to update.
    if (kind !== "fixedPrice") {
        return (
            <Box>
                {kindChoice}
                <TiersCard
                    data={data}
                    group={group}
                    canEdit={canEdit}
                    draft={props.tiersDraft}
                    onDraft={props.onTiersDraft}
                    boxRef={pricing}
                />
            </Box>
        );
    }
    return (
        <>
            {/* The same card and editor as an item's own pricing (PricingTab.tsx, PricingEditor.tsx): a fixed price, the price every
                item gets. */}
            <Box>
                {kindChoice}
                <PricingCard aside={`Prices its ${plural(pricedMembersOf(data, group.id).length, "product")}.`}>
                    <PricingEditor
                        data={data}
                        rule={rule}
                        tax={tax}
                        canEdit={canEdit}
                        price={setPrice}
                        taxNote="Every product it prices is taxed this way."
                        // Its kind is chosen above (Kind), not here.
                        pricedBy="The same price for every product it prices (one priced its own way keeps its own)."
                        editing={draft !== null}
                        // Its save bar; not being edited, in the same place, its prices and costs that need updating now (if any).
                        footer={draft ? <GroupSave data={data} group={group} draft={draft} onRunning={setRunning} onDone={() => setDraft(null)} /> : undefined}
                        boxRef={pricing}
                        onRule={change}
                        onMode={(mode) => change({ mode })}
                        onTax={changeTax}
                    />
                </PricingCard>
            </Box>
        </>
    );
}

/**
 * "9 items", "3 of 9 items" (narrowed: searched, or only some shown), or none yet (`text`); and, on hover (`title`), with how
 * many are retired ("9 items, 1 retired").
 */
function countText(shown: number, all: number, narrowed: boolean, retired: number, overriding: number) {
    if (!all) return { text: "No products in it yet.", title: undefined };
    const count = narrowed ? `${shown.toLocaleString()} of ${plural(all, "product")}` : plural(all, "product");
    const extras = [overriding ? plural(overriding, "price override") : "", retired ? `${retired.toLocaleString()} archived` : ""].filter(Boolean);
    // Just how many, so the header stays one line: the rest on hover (each row has its own badges).
    return { text: count, title: extras.length ? [count, ...extras].join(", ") : undefined };
}

/** Pricing as a rule of its own (its rule's fields only), e.g. to put back what an item had. */
const ruleOf = (p: ItemPricing): PricingRule => ({ mode: p.mode, markupValue: p.markupValue, basePrice: p.basePrice });

/** How a group's items can be listed (its Items tab). */
const ITEM_SORTS = [
    { value: "name", label: "Name" },
    { value: "number", label: "Item number" },
    { value: "price-high", label: "Price, high to low" },
    { value: "price-low", label: "Price, low to high" },
    { value: "update", label: "To update first" },
] as const;
type ItemSort = (typeof ITEM_SORTS)[number]["value"];

/** A group's items as its Items tab lists them: each with its price and name to update; sorted, retired last. */
/** A group's products as its Products tab lists them: each with its price to update, its cost to update, and what it overrides; sorted, archived last. */
function groupItemRows(data: PriceListData, group: GroupRow, sort: ItemSort, fmt: MoneyFormatter) {
    return (
        membersOf(data, group.id)
            .map((m) => {
                const itemRate = itemTax(data, m);
                const pricing = data.itemPricing[m.uuid];
                return {
                    m,
                    rate: itemRate.rate,
                    // Overriding the group's price: its price is its own business (its window), not the group's to update.
                    check: pricing?.overridesGroup ? null : needsCheck(m, itemRate, pricing),
                    // Its own pricing overriding the group's: how it's priced instead.
                    overrides: pricing?.overridesGroup ? describePricing(pricing, { includesTax: m.price_includes_taxes, tax: itemRate.tax }, fmt) : null,
                    noCost: missingCost(m, pricing),
                    // Its cost to update (supplierPrices.ts costUpdates), in its basis; none without See costs.
                    costTo: data.costUpdates[m.uuid] ?? null,
                };
            })
            .sort(itemSorter(sort, (m) => itemName(data, m)))
            // Retired ones last, however it's sorted (still using it: restored, they're priced by it again).
            .sort((a, b) => Number(!inUse(a.m)) - Number(!inUse(b.m)))
    );
}
type GroupItemRow = ReturnType<typeof groupItemRows>[number];

/** A price or cost to update on it (not a name: a group's are products, whose names are their own). */
const toUpdateRow = (r: Pick<GroupItemRow, "check" | "costTo">) => r.check !== null || r.costTo !== null;

/**
 * Its products: searched and added at the top, then each with its price (each follows the group at once: productsFollow.ts):
 * ticked, then taken out together.
 * Taking one out keeps its price (asked first). The list can be sorted, and narrowed (from Needs attention).
 */
function GroupItems({
    data,
    group,
    onOpenItem,
    attention,
    all,
    only,
    onOnly,
}: Readonly<{
    data: PriceListData;
    group: GroupRow;
    onOpenItem: (uuid: string) => void;
    /** Its items needing attention, with what (attention.ts groupAttention): beyond its own updates, for their rows' marks... */
    attention: ReadonlyMap<string, ItemProblem[]>;
    /** ...and every warning, for narrowing the list to one kind (`only`). */
    all: ReadonlyMap<string, ItemProblem[]>;
    /** Only those with one kind of warning listed (its chip in Needs attention; cleared here), or null: all. */
    only: ItemProblemId | null;
    onOnly: (only: ItemProblemId | null) => void;
}>) {
    const [sort, setSort] = useState<ItemSort>("name");
    // Searching its items (name, item number, full name).
    const [filter, setFilter] = useState("");
    // The items ticked, to update or take out together (the list's header).
    const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
    const fmt = useMoney();
    const rows = groupItemRows(data, group, sort, fmt);
    // Narrowed to a kind no item has any more: the whole list again.
    const narrowTo = useLiveOnly(only, all, onOnly);
    const shown = rows.filter((r) => withProblem(all, r.m.uuid, narrowTo) && matchesSearch(`${itemName(data, r.m)} ${r.m.item_number} ${r.m.name}`, filter));
    // Ticked, of those shown (a search narrows it).
    const picked = shown.filter((r) => selected.has(r.m.uuid));
    const clear = () => setSelected(new Set());
    const actions = usePickedActions(data, group, picked, clear);
    const tick = (uuid: string, on: boolean) =>
        setSelected((now) => {
            const next = new Set(now);
            if (on) next.add(uuid);
            else next.delete(uuid);
            return next;
        });
    const canEdit = can(data, "groups");
    return (
        <>
            {/* Searching its items, and adding some (a popup in the window: a search with an Add on each). */}
            <Box>
                <div className="flex items-center gap-2">
                    <label className="relative block min-w-0 flex-1">
                        <span className="sr-only">Search its items</span>
                        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                        <input className="input pl-9" placeholder="Search its items" value={filter} onChange={(e) => setFilter(e.target.value)} />
                    </label>
                    {canEdit ? <AddItems data={data} group={group} /> : null}
                </div>
            </Box>
            <Box className="py-0">
                {/* The list's own header, pinned while it scrolls and always the same height: the tick box for them all, then either how many
                    and how they're listed, or (some ticked) what's ticked and what to do with it, in the same place, so nothing moves. */}
                <div className="sticky top-0 z-10 -mx-4 flex h-11 items-center gap-3 border-b border-line bg-surface px-4 text-xs text-muted">
                    {canEdit && shown.length ? <TickAll shown={shown.length} picked={picked.length} disabled={actions.progress !== null} onAll={(all) => setSelected(all ? new Set(shown.map((r) => r.m.uuid)) : new Set())} /> : null}
                    {picked.length && canEdit ? (
                        <PickedBar picked={picked} actions={actions} />
                    ) : (
                        <ListBar
                            data={data}
                            group={group}
                            rows={rows}
                            shown={shown.length}
                            narrowed={narrowTo !== null || filter.trim() !== ""}
                            only={narrowTo}
                            kinds={problemKinds(all, (uuid) => itemNameOf(data, uuid))}
                            onOnly={onOnly}
                            sort={sort}
                            onSort={setSort}
                        />
                    )}
                </div>
                {rows.length > 0 && shown.length === 0 ? <p className="m-0 py-3 text-sm text-muted">No items in it match.</p> : null}
                {shown.length ? (
                    <ul className="-mx-4 divide-y divide-line">
                        {shown.map((row) => (
                            <ItemRow
                                key={row.m.uuid}
                                data={data}
                                group={group}
                                row={row}
                                canEdit={canEdit}
                                picked={selected.has(row.m.uuid)}
                                onPick={(on) => tick(row.m.uuid, on)}
                                onOpen={() => onOpenItem(row.m.uuid)}
                                problems={attention.get(row.m.uuid) ?? []}
                                saving={actions.saving(row.m.uuid)}
                                failure={actions.failure(row.m.uuid)}
                                // One run at a time: its ticks wait while one runs.
                                running={actions.progress !== null}
                            />
                        ))}
                    </ul>
                ) : null}
                <ErrorNotice error={actions.error} />
            </Box>
        </>
    );
}

/**
 * What can be done with the ticked products, one at a time, with how far it's got (`progress`): taken out of it (asked once: each
 * keeps the price it has, and its pricing as its own). Done: they're unticked.
 */
function usePickedActions(data: PriceListData, group: GroupRow, picked: readonly GroupItemRow[], clear: () => void) {
    const saver = usePriceListSaver();
    const confirm = useConfirm();
    const [error, setError] = useState<Error | null>(null);
    const [progress, setProgress] = useState<string | null>(null);
    // Where each is (its row shows a spinner while it's saved).
    const updates = useRowUpdates();
    const run = (uuids: string[], update: (uuid: string) => Promise<unknown>, what: string) => {
        setError(null);
        updates.clear();
        updateEach(uuids, update, what, setProgress, updates.track(what))
            .then(clear)
            .catch((err: Error) => setError(err))
            .finally(updates.settle);
    };
    const saving = (uuid: string) => [...updates.rows].some(([key, row]) => key.endsWith(`:${uuid}`) && row.state === "updating");
    // Turned down in the last run (kept until the next): why, for its row.
    const failure = (uuid: string) => {
        for (const [key, row] of updates.rows) if (key.endsWith(`:${uuid}`) && row.state === "failed") return row.error;
        return null;
    };
    const takeOut = async () => {
        const n = picked.length;
        const one = picked[0];
        const yes = await confirm({
            title: n === 1 ? `Take it out of ${group.name}?` : `Take ${n} products out of ${group.name}?`,
            subject: n === 1 && one ? itemName(data, one.m) : undefined,
            message: `${n === 1 ? "It keeps its price" : "Each keeps its price"}, and its pricing as its own.`,
            confirmLabel: "Take out",
            tone: "danger",
        });
        if (!yes) return;
        // Out of the group (its rule as it is now becomes its own), keeping the price it has if the group gave another.
        const keep = new Map(picked.map((r) => [r.m.uuid, r.check !== null]));
        run([...keep.keys()], (uuid) => saver.leaveGroup(uuid, keep.get(uuid) ?? false), "items");
    };
    return { progress, error, saving, failure, takeOut };
}

/** Adding items to the group: Add item, then a popup in the window, a search with an Add on each (any active item not in it). */
function AddItems({ data, group }: Readonly<{ data: PriceListData; group: GroupRow }>) {
    const saver = usePriceListSaver();
    const [adding, setAdding] = useState(false);
    // Each added one's pricing before, so Undo (a mis-click) puts it back exactly: its old group, its own rule, or a typed price.
    const before = useRef(new Map<string, ItemPricing | undefined>());
    const add = (uuid: string) => {
        before.current.set(uuid, data.itemPricing[uuid]);
        return saver.useGroup(uuid, group.id);
    };
    // Back as it was, in one step: on its old group or none, with its own rule if it had one.
    const undoAdd = (uuid: string) => {
        const was = before.current.get(uuid);
        return saver.putBack(uuid, was?.groupId ?? null, was && !usesGroup(was) ? ruleOf(was) : null);
    };
    return (
        <>
            <button type="button" className="btn-secondary shrink-0" onClick={() => setAdding(true)}>
                <Plus className="size-4" /> Add item
            </button>
            {adding ? (
                <WindowPopup top title={`Add products to ${group.name}`} onClose={() => setAdding(false)}>
                    <AddPicker inline placeholder="Search by name or item number" options={addChoices(data, group)} onAdd={add} onUndo={undoAdd} />
                </WindowPopup>
            ) : null}
        </>
    );
}

/** What can be added: any active item not in the group; one in another group says which, briefly (in full on hover), and takes a second click. */
function addChoices(data: PriceListData, group: GroupRow) {
    return data.materials
        // Products only: they're what's priced here (catalogue.ts).
        .filter((m) => m.active && isProduct(m) && data.itemPricing[m.uuid]?.groupId !== group.id)
        .map((m) => {
            const from = data.groups.find((g) => g.id === data.itemPricing[m.uuid]?.groupId);
            return {
                value: m.uuid,
                label: itemName(data, m),
                detail: m.item_number || undefined,
                note: from ? `In ${from.name}` : undefined,
                noteTitle: from ? `Using ${from.name}: adding it moves it to this one` : undefined,
                confirm: from !== undefined,
            };
        });
}

/** The list's tick box: none ticked, ticks every one shown; any ticked (some: shown part-ticked), unticks them all (as Gmail's). */
function TickAll({ shown, picked, disabled, onAll }: Readonly<{ shown: number; picked: number; disabled?: boolean; onAll: (all: boolean) => void }>) {
    const all = picked === shown;
    const label = picked ? "Untick them all" : "Tick every product shown";
    return (
        <input
            type="checkbox"
            className="size-4 shrink-0 accent-brand-600"
            aria-label={label}
            title={label}
            checked={all}
            disabled={disabled}
            ref={(el) => {
                if (el) el.indeterminate = picked > 0 && !all;
            }}
            onChange={() => onAll(picked === 0)}
        />
    );
}

/** The list's header with some ticked: how many, how far taking them out has got, and Take out of group. */
function PickedBar({ picked, actions }: Readonly<{ picked: readonly GroupItemRow[]; actions: ReturnType<typeof usePickedActions> }>) {
    return (
        <>
            {/* On one line, as the rest: unticked with the tick box beside it. */}
            <span className="shrink-0 font-medium whitespace-nowrap text-ink">{picked.length.toLocaleString()} selected</span>
            {actions.progress !== null ? (
                // Updating: just how far it's got (its row has a spinner), in the buttons' place: they can't be pressed meanwhile.
                <span className="ml-auto flex min-w-0 items-center gap-2 whitespace-nowrap">
                    <Spinner className="size-4 shrink-0" /> <span className="truncate">{actions.progress}</span>
                </span>
            ) : (
                <span className="ml-auto flex shrink-0 items-center gap-2 whitespace-nowrap">
                    <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={() => void actions.takeOut()}>
                        Take out of group
                    </button>
                </span>
            )}
        </>
    );
}

/**
 * The list's header with none ticked, on one line however narrow (each part kept whole, the count cut short first): how many,
 * what it's narrowed to (cleared with its ×), its items in the Materials list (keeping the windows open), and how it's sorted.
 */
function ListBar(
    props: Readonly<{
        data: PriceListData;
        group: GroupRow;
        rows: readonly GroupItemRow[];
        shown: number;
        narrowed: boolean;
        /** Narrowed to one kind of warning (its chip), and the kinds there are (ProblemChips.tsx). */
        only: ItemProblemId | null;
        kinds: readonly ProblemKind[];
        onOnly: (only: ItemProblemId | null) => void;
        sort: ItemSort;
        onSort: (sort: ItemSort) => void;
    }>,
) {
    const { rows, only } = props;
    // Just the group's items: the open windows go along with it (RecordWindow.tsx), this one too.
    const listQuery = new URLSearchParams({ inGroup: props.group.id });
    const counted = countText(props.shown, rows.length, props.narrowed, rows.filter((r) => !inUse(r.m)).length, rows.filter((r) => r.overrides !== null).length);
    return (
        <>
            <span className="min-w-0 truncate" title={counted.title}>
                {counted.text}
            </span>
            {/* Narrowed to one kind of warning (its chip): which, cleared with its × (ProblemChips.tsx, as every such list). */}
            <OnlyPill only={only} kinds={props.kinds} onClear={() => props.onOnly(null)} />
            {rows.length ? (
                <Link href={`/t/${meta.id}/products?${listQuery.toString()}`} className="ml-auto inline-flex shrink-0 items-center gap-0.5 whitespace-nowrap" title="See them in Products: their costs, markups and profits side by side">
                    Products <ArrowUpRight className="size-3.5" />
                </Link>
            ) : null}
            {rows.length > 1 ? (
                <span className="shrink-0 whitespace-nowrap">
                    <InlinePicker label="Sort by" options={ITEM_SORTS} value={props.sort} onChange={props.onSort} />
                </span>
            ) : null}
        </>
    );
}

/** An item's name as listed, by uuid (one that's gone: "An item"). */
function itemNameOf(data: PriceListData, uuid: string) {
    const m = data.materials.find((x) => x.uuid === uuid);
    return m ? itemName(data, m) : "An item";
}

/** What updating a row's price does (its hover text): "Update its price to $50.00 inc GST (taxed as the group says)". */
function updateTitle(check: NeedsCheck, basisTo: string | null, money: (amount: string) => string) {
    const taxed = basisTo ? " " + basisTo + " (taxed as it says)" : "";
    return `Update its price to ${money(check.target)}${taxed}`;
}

/**
 * One item in the group, on two lines. First its name (opens it), item number and what needs
 * attention or archived; on the right its price (RowPrice). Under them its full name as it reads in
 * ServiceM8 (RowName). A tick box first, to update or take it out with others from the list's header.
 */
function ItemRow(
    props: Readonly<{
        data: PriceListData;
        group: GroupRow;
        row: GroupItemRow;
        /** What needs attention on it (attention.ts), as in the Materials list: a mark, its row tinted. */
        problems: readonly ItemProblem[];
        canEdit: boolean;
        /** Ticked (to update or take out with others, from the list's header). */
        picked: boolean;
        onPick: (on: boolean) => void;
        onOpen: () => void;
        /** Being updated (with others ticked): a spinner by its name. */
        saving?: boolean;
        /** Turned down in the last update (why): said by its name. */
        failure?: string | null;
        /** An update's running: its tick box waits. */
        running?: boolean;
    }>,
) {
    const { data, row } = props;
    const { m } = row;
    const name = itemName(data, m);
    const danger = props.problems.some((p) => p.danger);
    const fmt = useMoney();
    // Its mark says what needs attention, and its cost to update (no badge for it: the group's own update, its row tinted).
    const marks = [...props.problems.map((p) => p.text), ...(row.costTo === null ? [] : [`Cost to update: ${fmt.money(m.cost)} → ${fmt.money(row.costTo)}`])];
    return (
        <li className={clsx("grid items-center gap-x-3 gap-y-1 px-4 py-2.5", props.canEdit ? "grid-cols-[auto_minmax(0,1fr)_auto]" : "grid-cols-[minmax(0,1fr)_auto]", rowTint(props.picked, props.problems.some(tintsRow) || toUpdateRow(row), danger))}>
            {props.canEdit ? (
                <input type="checkbox" className="size-4 accent-brand-600" aria-label={`Tick ${name}`} checked={props.picked} disabled={props.running} onChange={(e) => props.onPick(e.target.checked)} />
            ) : null}
            <span className="flex min-w-0 items-center gap-2">
                {props.saving ? <Spinner className="size-3.5 shrink-0" /> : null}
                <HiddenMark item={m} />
                <button type="button" className={clsx("link-button truncate text-left font-medium", retiredName(m))} onClick={props.onOpen}>
                    {name}
                </button>
                {/* Its item number after the name, small (amber when it has none: every item needs one). */}
                <span className={clsx("shrink-0 text-xs", m.item_number ? "text-muted" : "text-warning-ink")}>{m.item_number || "No item number"}</span>
                {/* An exception: in it, priced its own way (by its price, as production has it). */}
                {row.overrides !== null ? (
                    <Badge tone="blue" title={`Overrides the group's price, priced its own way: ${row.overrides}. Change it in its window.`}>
                        Price override
                    </Badge>
                ) : null}
                <NeedsAttention problems={marks} danger={danger} />
                {props.failure ? (
                    <span className="shrink-0 text-xs text-danger" title={props.failure}>
                        Couldn&apos;t update
                    </span>
                ) : null}
            </span>
            <RowPrice data={data} row={row} />
            {props.canEdit ? <span /> : null}
            <RowName row={row} tags={data.nameTags} />
        </li>
    );
}

/** A row's tint: ticked, else red with something wrong, else amber with something to see to (a price, cost or name to update too). */
function rowTint(picked: boolean, attention: boolean, danger: boolean) {
    if (picked) return "bg-brand-500/5";
    if (danger) return "bg-danger-soft";
    return attention ? "bg-warning-soft" : undefined;
}

/**
 * A row's price (the group's new one beside it when it gives another, with its tax when the group taxes it otherwise). No cost
 * and a cost to update are its row's attention mark and tint, said on hover there.
 */
function RowPrice({ data, row }: Readonly<{ data: PriceListData; row: GroupItemRow }>) {
    const fmt = useMoney();
    const { m, check } = row;
    const basis = basisLabel(m.price_includes_taxes, row.rate);
    const basisTo = check?.taxTo ? basisLabel(check.taxTo.includesTax, groupTaxBasis(data, check.taxTo).rate) : null;
    const why = check?.reason === "edited" ? "Its price was changed in ServiceM8" : "Its fixed price gives a new price";
    return (
        <span className="flex items-center justify-end gap-2 text-sm whitespace-nowrap tabular-nums">
            <span>
                {check ? (
                    <span title={`${why}: ${updateTitle(check, basisTo, fmt.money)}`}>
                        <span className="text-muted">
                            {fmt.money(m.price)}
                            {basisTo ? <span className="text-xs"> {basis}</span> : null}
                        </span>{" "}
                        → <span className="font-semibold text-warning-ink">{fmt.money(check.target)}</span>
                    </span>
                ) : (
                    fmt.money(m.price)
                )}{" "}
                <span className="text-xs text-muted">{basisTo ?? basis}</span>
            </span>
        </span>
    );
}

/** A row's full name as it reads in ServiceM8, all of it: each of its lines, wrapping as it needs, its tags coloured (as in the Materials list). */
function RowName({ row, tags }: Readonly<{ row: GroupItemRow; tags: NameTagConfig }>) {
    return (
        <div className="col-span-2 min-w-0 pl-6 text-xs whitespace-pre-line text-muted">
            <ServiceM8Name name={row.m.name} tags={tags} />
        </div>
    );
}

/** Sorting a group's items: by name, item number, price either way, or those to update (price or cost) first; ties by name. */
function itemSorter(sort: ItemSort, nameOf: (m: MaterialRow) => string) {
    type Row = { m: MaterialRow; check: NeedsCheck | null; costTo: string | null };
    const byName = (a: Row, b: Row) => nameOf(a.m).localeCompare(nameOf(b.m));
    const price = (r: Row) => Number(r.m.price) || 0;
    const toUpdate = (r: Row) => (toUpdateRow(r) ? 0 : 1);
    return (a: Row, b: Row) => {
        let diff = 0;
        if (sort === "number") diff = a.m.item_number.localeCompare(b.m.item_number, undefined, { numeric: true });
        else if (sort === "price-high") diff = price(b) - price(a);
        else if (sort === "price-low") diff = price(a) - price(b);
        else if (sort === "update") diff = toUpdate(a) - toUpdate(b);
        return diff || byName(a, b);
    };
}
